<template>
  <themed-modal
    name="edit-client"
    title="Edit Client"
    size="md"
    @closed="handleClosed"
  >
    <themed-form
      :fields="clientFields"
      :model-value="client"
      :errors="errors"
      layout="grid"
      :columns="12"
      @input="client = $event"
    />

    <template #footer>
      <div class="modal-actions">
        <themed-button
          variant="outline"
          @click="hideModal"
          :disabled="loading"
        >
          Close
        </themed-button>
        <themed-button
          @click="save"
          :disabled="loading"
        >
          {{ loading ? 'Saving...' : 'Save Client' }}
        </themed-button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
import { mapState } from 'vuex';
import ThemedModal from '../ThemedModal.vue';
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';

export default {
  name: 'EditClient',

  components: {
    ThemedModal,
    ThemedForm,
    ThemedButton
  },

  props: {
    clientId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      client: {
        name: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: 16,
        npv_discount: 10
      },
      countries: [],
      states: [],
      errors: {},
      loading: false
    };
  },

  computed: {
    ...mapState(['clients']),

    clientFields() {
      return [
        {
          name: 'name',
          type: 'input',
          label: 'Name',
          required: true,
          col: 12
        },
        {
          name: 'address',
          type: 'input',
          label: 'Address',
          required: true,
          col: 12
        },
        {
          name: 'city',
          type: 'input',
          label: 'City',
          required: true,
          col: 4
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          options: this.stateOptions,
          required: true,
          col: 4
        },
        {
          name: 'postal_code',
          type: 'input',
          label: 'Postal Code',
          required: true,
          col: 4
        },
        {
          name: 'country_id',
          type: 'select',
          label: 'Country',
          options: this.countryOptions,
          required: true,
          col: 12
        },
        {
          name: 'npv_discount',
          type: 'slider',
          label: 'NPV Discount Rate (%)',
          helpText: 'Applied to every site in this portfolio. A single rate keeps Total Portfolio NPV a valid sum.',
          componentProps: { min: 0, max: 25, step: 1, showValue: true },
          col: 12
        }
      ];
    },

    countryOptions() {
      return this.countries.map(country => ({
        value: country.id,
        label: country.name
      }));
    },

    stateOptions() {
      return this.states.map(state => ({
        value: state.state,
        label: state.state
      }));
    }
  },

  created() {
    this.loadClient();
    this.loadAddressInfo();
  },

  methods: {
    loadClient() {
      const clientData = _.find(this.clients.all, (client) => client.id == this.clientId);
      if (clientData) {
        this.client = _.clone(clientData);
      }
    },

    async loadAddressInfo() {
      try {
        const response = await axios.get('/api/address-info');
        this.countries = response.data.countries;
        this.states = response.data.states;
      } catch (error) {
        console.error('Error loading address info:', error);
      }
    },

    hideModal() {
      this.$modal.hide('edit-client');
    },

    handleClosed() {
      this.errors = {};
      this.loading = false;
    },

    async save() {
      this.loading = true;
      this.errors = {};

      try {
        const response = await axios.patch('/clients/' + this.client.id, this.client);

        this.$emit('client-updated', response.data.client);

        this.$notify({
          type: 'success',
          title: 'Success',
          text: response.data.message
        });

        this.hideModal();
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }

        const message = error.response?.data?.message || 'Failed to update client';
        this.$notify({
          type: 'error',
          title: 'Error',
          text: message
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
